import type { AdminReportDetail } from '../../../../api/DigiobcanApi';
import { FormattedText } from '../../../../components/FormattedText';
import SimpleMap from '../../../../components/SimpleMap';
import { shortId } from '../../../../lib/ids';
import { __, formatDateTime, hasLocation } from '../../../../utils';
import EnvelopeAttachments from './EnvelopeAttachments';
import ReportTiles from './ReportTiles';
import ReportVisibilityToggle from './ReportVisibilityToggle';
import {
  Box,
  Card,
  Divider,
  Stack,
  Tooltip,
  Typography,
} from '@presov.sk/presov-ui-react';
import { encode } from 'pluscodes';
import React from 'react';
import { Link } from 'react-router';

type Props = {
  report: AdminReportDetail;
  onChange: () => Promise<unknown>;
  onPreview: (media: AdminReportDetail['media'][number]) => void;
  /**
   * Rozloženie karty:
   *  - `wide` - karta na celú šírku, telo v dvoch stĺpcoch (užší desktop),
   *  - `column` - úzky stĺpec vedľa nálezov (široký desktop),
   *  - `mobile` - jedna obrazovka, bez karty.
   */
  variant?: 'wide' | 'column' | 'mobile';
  /**
   * Archivácia podnetu ([[ReportArchive]]), ako slot a nie ako natvrdo
   * vykreslený komponent: potrebuje oprávnenie aj odchod na zoznam, a ani
   * jedno obálka nepozná. Sama len rozhoduje, **kam** to patrí - pod mapu,
   * na konec karty.
   *
   * Komponent nevykreslí nič, keď podnet už archivovaný je alebo keď na to
   * volajúci nemá právo, takže si oddeľovač nesie sám a obálke tu nezostane
   * visieť prázdna linka.
   */
  archiveAction?: React.ReactNode;
  /**
   * Úradník podnet len vidí - nemá kategóriu, v ktorej by ho riešil
   * (`pruser:report-categories:read-all`). Obálka to nevie zistiť sama:
   * je to vec všetkých nálezov podnetu, ktoré drží stránka.
   */
  readOnly?: boolean;
};

/**
 * Šírka pravého stĺpca širokej karty - dlaždíc s číslami a mapy pod nimi.
 *
 * Číslo pochádza z čias, keď v tom stĺpci stál aj prepínač nevhodnosti (ten je
 * dnes v hlavičke stránky) a jeho vysvetlenie sa muselo zmestiť na jeden
 * riadok. Dlaždiciam a mape sedí, tak zostalo.
 */
const SIDE_COLUMN_WIDTH = 380;

/**
 * Šírka stĺpca obálky vo variante `column`.
 *
 * Obsah karty v ňom vychádza na 508 px - toľko potrebuje popis od občana, aby
 * sa nelámal po štyroch slovách, a mapa, aby sa z nej dala prečítať ulica.
 * Nálezy vedľa zostávajú **širšie**, a to výrazne: pri 1440 px im ostáva
 * ~880 px, pri 1600 px ~1040 px. Je to zámer - v nich sa píše, tu sa číta.
 */
export const ENVELOPE_COLUMN_WIDTH = 540;

/**
 * Obálka podnetu - všetko, čo platí pre podnet ako celok. Riešenie sa deje
 * o úroveň nižšie, na nálezoch, takže tu zámerne nie je nič, čo by menilo stav.
 *
 * Dve odchýlky od dizajnového balíka, obe preto, že pre ne nie sú dáta:
 *  - **ulica** sa nezobrazuje. Podnet nesie súradnice a z nich odvodený
 *    plus-kód; reverzný geokóder appka nepoužíva nikde a doťahovať ho sem by
 *    bola samostatná zmena.
 *  - **hlasy sú jedno číslo**, nie „dôležité · naliehavé". Typy hlasov zrušila
 *    migrácia 000011 a 000012 - dizajn kreslí stav spred nej.
 */
const ReportEnvelope: React.FC<Props> = ({
  report,
  onChange,
  onPreview,
  variant = 'wide',
  archiveAction,
  readOnly = false,
}) => {
  // Zvyšok súboru sa pýta na `compact`, nie na variant: mobil je jediné
  // rozloženie, ktoré mení aj vnútro blokov (prílohy scrollujú od okraja
  // obrazovky, tlačidlá stoja pod pásom), inde ide len o poradie a šírky.
  const compact = variant === 'mobile';

  /**
   * Výška, na ktorú sa smie zakotvená karta natiahnuť.
   *
   * Nie je to konštanta: `position: sticky; top: 0` znamená, že zakotvená karta
   * začína presne na hornej hrane skrolovacieho obalu, takže jej maximum je
   * **výška toho obalu**. Ten patrí shellu, takže sa hľadá za behu - hocijaké
   * `calc(100vh - Xrem)` by bolo hádanie výšky hlavičky appky, žltého pásu
   * a hlavičky stránky dokopy, a rozišlo by sa pri prvej zmene ktorejkoľvek
   * z nich.
   *
   * Kým sa nezmeria, karta výšku obmedzenú nemá - radšej raz presiahnuť než
   * bliknúť orezaná.
   */
  const cardRef = React.useRef<HTMLDivElement>(null);
  const [stickyMaxHeight, setStickyMaxHeight] = React.useState<number>();

  React.useEffect(() => {
    if (variant !== 'column') return;

    const measure = () => {
      let node = cardRef.current?.parentElement ?? null;
      while (node) {
        const { overflowY } = window.getComputedStyle(node);
        if (overflowY === 'auto' || overflowY === 'scroll') {
          setStickyMaxHeight(node.clientHeight);
          return;
        }
        node = node.parentElement;
      }
      // Skroluje samotný dokument - vtedy je obalom okno.
      setStickyMaxHeight(window.innerHeight);
    };

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [variant]);

  const plusCode = React.useMemo(() => {
    if (!hasLocation(report)) return '';
    return encode({
      latitude: report.location.coordinates[1],
      longitude: report.location.coordinates[0],
    });
  }, [report]);

  /**
   * Nadpis karty v úzkom stĺpci - iba názov podnetu.
   *
   * Popisok „Podnet občana" nad ním chvíľu stál (karty nálezov vedľa majú
   * hlavičku rovnakého tvaru), ale hovoril to, čo je z obsahu karty zrejmé -
   * popis, prílohy a miesto sú tá jediná vec na obrazovke, ktorú nepísal úrad.
   *
   * Dátum podania je vlastný riadok pod názvom, nie vedľa neho - vedľa s ním
   * splýval do jednej vety. Linka pod ním oddeľuje identitu podnetu od toho,
   * čo k nemu občan napísal.
   */
  const envelopeHeading = (
    <Stack gap={0.25} sx={{ minWidth: 0 }}>
      <Typography
        variant='h6'
        fontWeight={700}
        sx={{ wordBreak: 'break-word' }}
      >
        {__(report.title)}
      </Typography>
      <Typography variant='caption' color='text.secondary'>
        {`${__('Podané')} ${formatDateTime(new Date(report.inserted_at))}`}
      </Typography>
      <Divider sx={{ mt: 1.25 }} />
    </Stack>
  );

  // Názov a dátum podania - prvý blok obsahu pod hlavičkou stránky. Hlavička
  // nesie identifikátor podnetu, názov teda musí byť tu, a to celý: na mobile
  // sa v nej skracoval trojbodkou. Dátum je vlastný riadok pod ním, vedľa neho
  // s ním splýval do jednej vety.
  const titleBlock = (
    <Stack gap={0.25} sx={{ minWidth: 0 }}>
      <Typography
        variant={compact ? 'h6' : 'subtitle1'}
        fontWeight={700}
        sx={{ wordBreak: 'break-word' }}
      >
        {__(report.title)}
      </Typography>
      <Typography variant='caption' color='text.secondary'>
        {`${__('Podané')} ${formatDateTime(new Date(report.inserted_at))}`}
      </Typography>
    </Stack>
  );

  // Odkaz „Verejný detail" tu bol kedysi a je preč zámerne: úrad má v tejto
  // administrácii všetko, čo o podnete vidí občan, a ešte viac. Prepínač
  // nevhodnosti si obálka naopak necháva vo všetkých rozloženiach - je to
  // jediná akcia nad celým podnetom a v hlavičke stránky, kde chvíľu stál,
  // nemal pri sebe nič, čoho by sa týkal.

  // Lehota, Hlasy a prepínač nevhodnosti sú na desktope **v hlavičke stránky**
  // ([ReportTiles](ReportTiles.tsx), [ReportVisibilityToggle](ReportVisibilityToggle.tsx)):
  // platia pre podnet ako celok, nie pre obálku, a v hlavičke stoja vedľa
  // identifikátora, ktorého sa týkajú. Mobilná hlavička nesie len titul, takže
  // tam ostávajú v obálke - je to celá stránka, nič sa tým neprekrýva.

  // Mapa musí byť taká vysoká, aby sa z nej dalo prečítať, kde to je - pri
  // 132 px to bola len dlaždica s bodkou. V úzkom stĺpci má **pevných 240 px**:
  // pod ňou stojí prepínač nevhodnosti, ktorý sa karte drží pri spodnej hrane,
  // takže mapa si zvyšok výšky vziať nemôže (`flex: 1` by ho vytlačil mimo
  // obrazovku).
  const mapBox = (
    <Box
      sx={{
        height: compact ? 200 : variant === 'column' ? 240 : 300,
        borderRadius: 1,
        overflow: 'hidden',
        border: (theme) => `1px solid ${theme.palette.divider}`,
        backgroundColor: 'background.default',
      }}
    >
      {hasLocation(report) ? (
        <SimpleMap
          location={report.location.coordinates as [number, number]}
          zoom={16}
        />
      ) : (
        <Stack
          sx={{ height: '100%' }}
          alignItems='center'
          justifyContent='center'
        >
          <Typography variant='caption' color='text.secondary'>
            {__('Podnet nemá priradenú lokalitu')}
          </Typography>
        </Stack>
      )}
    </Box>
  );

  // Plus kód vedie na **mapu podnetov v appke**, nie do Google Máp: úradník
  // z neho chce vidieť, čo sa v okolí ešte nahlásilo (duplicity, súvisiace
  // veci), a to Google nevie.
  //
  // Verejný podnet sa otvorí rovno ako `/podnety/<ID>`: mapa priletí na jeho
  // značku a vedľa je jeho detail, teda presne tá poloha aj s okolím.
  // Nevhodný či archivovaný podnet ale verejná mapa neukáže a detail by ostal
  // prázdny - ten vedie na okolie cez **súradnice, nie plus kód**. Plus kód
  // z adresy hlavná stránka overuje proti hranici mesta a bod mimo nej
  // preloží na stred Prešova; súradnice to obídu a nestratia presnosť (~14 m).
  const nearbyHref = !hasLocation(report)
    ? undefined
    : report.explicit || report.archived_at
      ? `/?lat=${report.location.coordinates[1]}&lon=${report.location.coordinates[0]}`
      : `/podnety/${shortId(report.id)}`;

  const plusCodeLink =
    plusCode && nearbyHref ? (
      <Tooltip title={__('Zobraziť podnet na mape')}>
        <Typography
          variant='caption'
          component={Link}
          to={nearbyHref}
          // Farba odkazu z dizajn manuálu, nie modrá prehliadača - holý
          // `Link` z react-routera žiadnu vlastnú nemá.
          sx={{
            fontFamily: 'ui-monospace, Menlo, monospace',
            fontWeight: 600,
            color: 'primary.main',
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          {plusCode}
        </Typography>
      </Tooltip>
    ) : null;

  // Mapa a plus kód pod sebou - pravý stĺpec širokej karty a rovnaký blok
  // na mobile. Dlaždice s číslami sú v ňom už len na mobile; na desktope ich
  // nesie hlavička stránky.
  const locationBlock = (
    <Stack gap={1} sx={{ minWidth: 0 }}>
      {compact && <ReportTiles report={report} />}
      {mapBox}
      {plusCodeLink && (
        <Stack direction='row' justifyContent='flex-end'>
          {plusCodeLink}
        </Stack>
      )}
      {/* Na mobile nie: tam za týmto blokom stoja ešte prílohy, takže by
          archivácia sedela do stredu karty. Vykresľuje sa až na jej konci. */}
      {!compact && archiveAction}
    </Stack>
  );

  const textBlock = (
    <Stack gap={2} sx={{ flex: 1, minWidth: 0 }}>
      {/* Text autora podnetu sa nikdy neprepisuje - vrátane preklepov. */}
      <Box sx={{ maxWidth: '78ch' }}>
        <FormattedText text={report.description ?? ''} />
      </Box>
      <Box sx={{ mt: 'auto' }}>
        <EnvelopeAttachments
          report={report}
          onChange={onChange}
          onPreview={onPreview}
          compact={compact}
          readOnly={readOnly}
        />
      </Box>
    </Stack>
  );

  if (compact) {
    return (
      <Stack
        gap={1.75}
        sx={{
          px: 1.75,
          py: 1.75,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        {titleBlock}
        <Divider />
        <ReportVisibilityToggle
          report={report}
          onSuccess={onChange}
          readOnly={readOnly}
        />
        <Box sx={{ maxWidth: '78ch' }}>
          <FormattedText text={report.description ?? ''} />
        </Box>
        {locationBlock}
        <EnvelopeAttachments
          report={report}
          onChange={onChange}
          onPreview={onPreview}
          compact
          readOnly={readOnly}
        />
        {archiveAction}
      </Stack>
    );
  }

  // ---- úzky stĺpec vedľa nálezov -----------------------------------------
  //
  // Poradie je iné než v širokej karte, lebo jeden stĺpec sa číta zhora nadol:
  // najprv čo sa stalo (názov, popis), potom čísla, potom dôkazy a naspodku
  // miesto. Prepínač nevhodnosti je posledný a drží sa spodnej hrany karty -
  // je to jediná vec v nej, ktorá niečo mení, a nemá stáť medzi tým, čo sa
  // len číta.
  //
  // Odsadenie je to isté ako v kartách nálezov vedľa (`px: 2.25`, `py: 1.75`),
  // aby obálka a nález boli jedna obrazovka a nie dve karty s vlastným
  // pravidlom. Nesie ho vnútorný `Stack`, nie karta - `Card` z knižnice si
  // prináša vlastných 16 px a s paddingom na oboch sa odsadenie zdvojí.
  //
  // Karta je **zakotvená pri hornej hrane**: nálezov býva na výšku niekoľko
  // obrazoviek a úradník pri čítaní vyjadrení potrebuje mať popis, prílohy
  // a mapu stále pred sebou.
  //
  // Vysoká karta skroluje vo svojom vnútri a **potom sa skrolovanie prelieva
  // na stránku** - to je predvolené správanie prehliadača (`overscroll-behavior`
  // sa nenastavuje práve preto). Koliesko nad kartou teda najprv dočíta ju
  // a potom pokračuje v nálezoch; koliesko kdekoľvek inde hýbe rovno stránkou.
  // Bez `maxHeight` by sa spodok vysokej zakotvenej karty nedal zobraziť vôbec.
  if (variant === 'column') {
    return (
      <Card
        ref={cardRef}
        sx={{
          p: 0,
          position: 'sticky',
          top: 0,
          maxHeight: stickyMaxHeight,
          overflowY: 'auto',
        }}
      >
        <Stack gap={1.75} sx={{ px: 2.25, py: 1.75 }}>
          {envelopeHeading}
          {/* Text autora podnetu sa nikdy neprepisuje - vrátane preklepov. */}
          <FormattedText text={report.description ?? ''} />
          <EnvelopeAttachments
            report={report}
            onChange={onChange}
            onPreview={onPreview}
          />
          <Stack gap={1}>
            {/* Hlavička sekcie v rovnakom tvare ako Prílohy nad ňou; plus kód
                je odkaz na miesto, tak stojí na jej konci a nie pod mapou.
                Návrh tu má aj ulicu - tá sa nekreslí, appka reverzný geokóder
                nepoužíva a podnet nesie len súradnice. */}
            <Stack direction='row' alignItems='center' gap={1}>
              <Typography
                variant='overline'
                sx={{ fontWeight: 700, letterSpacing: '.05em', flex: 1 }}
              >
                {__('Miesto')}
              </Typography>
              {plusCodeLink}
            </Stack>
            {mapBox}
            {archiveAction}
          </Stack>
        </Stack>
      </Card>
    );
  }

  // Karta má hlavičku s identitou podnetu a pod linkou telo v dvoch stĺpcoch:
  // vľavo text a prílohy, vpravo čísla s mapou. Linka ide **cez celú kartu**,
  // takže oba stĺpce začínajú na tej istej výške bez dopočítavania.
  return (
    <Card sx={{ p: 2.5 }}>
      {titleBlock}

      <Divider sx={{ mt: 1.25, mb: 1.75 }} />

      <Stack direction='row' gap={2.75} alignItems='stretch'>
        {textBlock}
        <Box sx={{ width: SIDE_COLUMN_WIDTH, flex: 'none' }}>
          {locationBlock}
        </Box>
      </Stack>
    </Card>
  );
};

export default ReportEnvelope;
